/**
 * Client-side email and password rules for the consumer auth screens.
 *
 * This is a deliberate port of `validate_consumer_email` / `validate_consumer_password`
 * (crates/api/src/routes/auth.rs), not a looser «good enough» check, and it is stricter than
 * either mobile client. The reason is the registration throttle: the server records the attempt
 * BEFORE it validates anything (auth.rs:411 comes before :415), so a mistyped address costs one
 * of five attempts per hour per IP — three typos spend 60% of an hour's budget. Every rule
 * enforced here is an attempt not spent. The same logic applies to the reset request (3 per
 * 15 minutes) and to login, where a rejected attempt walks the account toward its 24-hour lock.
 *
 * Two details that look like nitpicking and are not:
 *
 * - **Lengths count code points, not UTF-16 units.** Rust counts `chars()`, and `'😀'.length` is
 *   2 in JavaScript against 1 in Rust. Measuring with `.length` would reject input the server
 *   accepts, which is the one failure mode a client-side copy of a server rule must never have.
 * - **The domain rule is «a non-empty label on each side of the LAST dot»** — that is what
 *   `rsplit_once('.')` does. So «you@mail.» and «you@.ru» are refused while «a@b.c.ru» is not.
 *
 * There is deliberately no check on password composition: the backend has none either
 * (auth.rs:1350-1362 in full), and a client that demanded a digit would be inventing a policy
 * the server does not have.
 *
 * The wording is the mobile clients' own (docs/20 §A3), so somebody who registered on the phone
 * and signs in on the web is never told the same rule in two different sentences.
 *
 * Normalization is not here on purpose: trimming and lower-casing into the canonical
 * `users.username` form happens once, in the transport layer (entities/session/api.ts), so there
 * is exactly one place that decides what is actually sent.
 */

/** RFC 5321's practical bound for a whole address, counted after trimming. */
export const MAX_EMAIL_CHARS = 254;

export const MIN_PASSWORD_CHARS = 8;

/** Exists to bound argon2's cost, not to protect the user — see auth.rs:1286-1290. */
export const MAX_PASSWORD_CHARS = 128;

/** Code points, the unit Rust's `chars().count()` uses. */
function charCount(value: string): number {
  return [...value].length;
}

const INVALID_EMAIL = 'Проверьте email — похоже, в адресе ошибка.';

/**
 * `null` when the address would pass the server's parser, otherwise the sentence to show.
 *
 * All format failures share one message on purpose. The server distinguishes six of them, but
 * every one of those distinctions is «your address has a typo» told at a level of detail nobody
 * asked for, and its own texts are English (`must contain exactly one '@'`).
 */
export function validateEmail(raw: string): string | null {
  const email = raw.trim();
  if (email.length === 0) return 'Введите email';
  if (charCount(email) > MAX_EMAIL_CHARS) return INVALID_EMAIL;
  // JS `\s` and Rust `char::is_whitespace` agree on everything a person can type; the trim above
  // has already removed the surrounding ones, so anything left is inside the address.
  if (/\s/u.test(email)) return INVALID_EMAIL;

  const at = email.indexOf('@');
  if (at < 0) return INVALID_EMAIL;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (local.length === 0 || domain.length === 0 || domain.includes('@')) return INVALID_EMAIL;

  const lastDot = domain.lastIndexOf('.');
  if (lastDot < 0) return INVALID_EMAIL;
  const label = domain.slice(0, lastDot);
  const tld = domain.slice(lastDot + 1);
  if (label.length === 0 || tld.length === 0) return INVALID_EMAIL;

  return null;
}

/**
 * `null` when the password is within 8…128 code points.
 *
 * Both bounds are checked locally, which is the iOS behaviour rather than Android's — Android
 * lets a 129-character password travel to the server and come back `weak_password`, spending a
 * registration attempt to learn something the client already knew.
 */
export function validatePassword(raw: string): string | null {
  const count = charCount(raw);
  if (count < MIN_PASSWORD_CHARS) return 'Пароль — минимум 8 символов';
  if (count > MAX_PASSWORD_CHARS) return 'Пароль должен быть от 8 до 128 символов';
  return null;
}

/**
 * Whether the submit button may light up at all.
 *
 * Split from `validatePassword` because the two answer different questions: this one gates a
 * control the user has not pressed yet, so it must not be the thing that explains the rule —
 * the field's own placeholder does that.
 */
export function isPasswordLengthValid(raw: string): boolean {
  return validatePassword(raw) === null;
}

/**
 * True once the password is past the ceiling.
 *
 * The floor and the ceiling need different treatment in the UI: a too-short password is a
 * half-typed one and explaining it while the person is still typing is nagging, while a
 * too-long one is the only way to be stuck with a disabled button and no idea why.
 */
export function isPasswordTooLong(raw: string): boolean {
  return charCount(raw) > MAX_PASSWORD_CHARS;
}
